import React, { useState, useEffect, useCallback } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";

const STEPS = [
  {
    id: "welcome",
    title: "Welcome to Arvay",
    body: "Your personal media intelligence terminal. This quick tour covers the key features — it takes under a minute.",
    target: null,
  },
  {
    id: "tabs",
    title: "News Feed Tabs",
    body: "Navigate between content categories — Innovation, Global, Regional, Shares/Stocks, Horoscope, and Community. Each tab pulls from curated real-time sources.",
    target: "[data-tour-id='tab-bar']",
  },
  {
    id: "filter",
    title: "Keyword Filters",
    body: "On any tab, type a keyword and press Add to create a persistent filter chip. Only articles mentioning your keyword will appear. Add multiple chips for broader coverage — chips use OR logic.",
    target: "[data-testid='tab-keyword-filter']",
  },
  {
    id: "sources",
    title: "Source Filter",
    body: "Filter articles by publication using the chips below the search bar. Click any source to isolate it. Use the Reset Filters pill to clear your selection.",
    target: "[data-tour-id='source-chips']",
  },
  {
    id: "topics",
    title: "My Topics",
    body: "Track keywords you care about. Switch to the My Topics tab, add your keywords, and Arvay will surface matching articles from across the feed in real time.",
    target: "[data-tour-id='my-topics-tab']",
  },
  {
    id: "account",
    title: "Profile & Account",
    body: "Save articles to bookmarks, manage your display name, suggest new sources, and access admin tools — all from the toolbar icons at the top.",
    target: "[data-tour-id='toolbar']",
  },
];

const PAD = 10; // px of padding around the spotlight
const TIP_W = 340;

export default function OnboardingTour({ onDone }) {
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);

  const current = STEPS[step];

  const measure = useCallback(() => {
    if (!current.target) { setRect(null); return; }
    const el = document.querySelector(current.target);
    if (!el) { setRect(null); return; }
    const r = el.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, [current.target]);

  useEffect(() => {
    measure();
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("scroll", measure, true);
      window.removeEventListener("resize", measure);
    };
  }, [measure]);

  const spot = rect
    ? { top: rect.top - PAD, left: rect.left - PAD, w: rect.width + PAD * 2, h: rect.height + PAD * 2 }
    : null;

  // Clip-path creates a rectangular "hole" in the dark overlay exactly over the target
  const clip = spot
    ? `polygon(0% 0%, 0% 100%, ${spot.left}px 100%, ${spot.left}px ${spot.top}px, ${spot.left + spot.w}px ${spot.top}px, ${spot.left + spot.w}px ${spot.top + spot.h}px, ${spot.left}px ${spot.top + spot.h}px, ${spot.left}px 100%, 100% 100%, 100% 0%)`
    : "none";

  // Tooltip positioning — prefer below target, fall back above if near bottom
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let tipTop, tipLeft;

  if (spot) {
    const below = spot.top + spot.h + 16;
    const above = spot.top - 180;
    tipTop = below + 170 < vh ? below : Math.max(16, above);
    tipLeft = Math.max(16, Math.min(spot.left + spot.w / 2 - TIP_W / 2, vw - TIP_W - 16));
  } else {
    tipTop = vh / 2 - 110;
    tipLeft = vw / 2 - TIP_W / 2;
  }

  const goNext = () => (step < STEPS.length - 1 ? setStep(s => s + 1) : onDone());
  const goBack = () => { if (step > 0) setStep(s => s - 1); };

  return (
    <>
      {/* Dark backdrop with spotlight cutout */}
      <div
        style={{
          position: "fixed", inset: 0, zIndex: 9990,
          background: "rgba(0,0,0,0.62)",
          clipPath: clip,
          WebkitClipPath: clip, // Safari
          transition: "clip-path 0.3s ease",
          pointerEvents: "auto",
        }}
        onClick={onDone}
        aria-hidden="true"
      />

      {/* Spotlight ring around target */}
      {spot && (
        <div
          style={{
            position: "fixed",
            top: spot.top, left: spot.left,
            width: spot.w, height: spot.h,
            border: "2px solid rgba(255,255,255,0.9)",
            borderRadius: 4,
            zIndex: 9991,
            pointerEvents: "none",
            transition: "all 0.3s ease",
            boxShadow: "0 0 0 1px rgba(0,0,0,0.25)",
          }}
        />
      )}

      {/* Tooltip card */}
      <div
        style={{
          position: "fixed",
          top: tipTop, left: Math.max(16, Math.min(tipLeft, vw - TIP_W - 16)),
          width: Math.min(TIP_W, vw - 32),
          zIndex: 9999,
          background: "#fff",
          border: "1px solid #1A1A1A",
          boxShadow: "0 12px 40px rgba(0,0,0,0.22)",
          padding: "20px 22px 18px",
          transition: "top 0.3s ease, left 0.3s ease",
        }}
        role="dialog"
        aria-label={`Tour step ${step + 1}: ${current.title}`}
      >
        {/* Step dots */}
        <div style={{ display: "flex", gap: 4, marginBottom: 14 }}>
          {STEPS.map((_, i) => (
            <div
              key={i}
              style={{
                height: 4, borderRadius: 2,
                width: i === step ? 20 : 6,
                background: i === step ? "#000" : i < step ? "#aaa" : "#ddd",
                transition: "all 0.25s ease",
              }}
            />
          ))}
        </div>

        <h3 style={{
          fontFamily: "'Cormorant Garamond', Georgia, serif",
          fontSize: 18, fontWeight: 600, color: "#000",
          marginBottom: 8, lineHeight: 1.3,
        }}>
          {current.title}
        </h3>
        <p style={{
          fontFamily: "system-ui, -apple-system, sans-serif",
          fontSize: 13, color: "#555", lineHeight: 1.65, marginBottom: 20,
        }}>
          {current.body}
        </p>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          {/* Skip link */}
          <button
            onClick={onDone}
            style={{
              fontSize: 10, color: "#aaa", background: "none", border: "none",
              cursor: "pointer", textTransform: "uppercase", letterSpacing: "0.12em",
              padding: "4px 0",
            }}
          >
            Skip tour
          </button>

          <div style={{ display: "flex", gap: 8 }}>
            {step > 0 && (
              <button
                onClick={goBack}
                style={{
                  display: "flex", alignItems: "center", gap: 5,
                  padding: "7px 14px", background: "transparent",
                  border: "1px solid #ccc", cursor: "pointer",
                  fontSize: 11, fontFamily: "sans-serif", color: "#555",
                }}
              >
                <ArrowLeft size={12} /> Back
              </button>
            )}
            <button
              onClick={goNext}
              style={{
                display: "flex", alignItems: "center", gap: 5,
                padding: "7px 18px", background: "#000",
                border: "none", cursor: "pointer",
                fontSize: 11, color: "#fff",
                fontFamily: "sans-serif",
                letterSpacing: "0.08em",
              }}
            >
              {step === STEPS.length - 1 ? "Done" : "Next"}
              {step < STEPS.length - 1 && <ArrowRight size={12} />}
            </button>
          </div>
        </div>

        {/* Counter */}
        <span style={{
          position: "absolute", top: 14, right: 16,
          fontSize: 10, color: "#ccc", fontFamily: "sans-serif",
        }}>
          {step + 1}&thinsp;/&thinsp;{STEPS.length}
        </span>
      </div>
    </>
  );
}
